import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { jwtDecode } from 'jwt-decode'
import { useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/api/auth'

type UserRole = 'TenantAdmin' | 'ClientManager' | 'StaffMember' | 'SuperAdmin' | 'Trainer'

interface AuthUser {
  userId: string
  tenantId: string
  email: string
  role: UserRole
  trainerId?: string
  imp?: string // set when this is an impersonation session (value = superadmin email)
}

interface AuthContextValue {
  user: AuthUser | null
  login: (email: string, password: string) => Promise<void>
  loginWithToken: (accessToken: string) => void
  logout: () => Promise<void>
  impersonate: (token: string, tenantName: string) => void
  exitImpersonation: () => void
  impersonatedTenantName: string | null
}

interface JwtPayload {
  sub: string
  tenantId: string
  email: string
  role: UserRole
  trainerId?: string
  imp?: string
}

const AuthContext = createContext<AuthContextValue | null>(null)

function decodeUser(token: string): AuthUser {
  const payload = jwtDecode<JwtPayload>(token)
  return {
    userId: payload.sub,
    tenantId: payload.tenantId,
    email: payload.email,
    role: payload.role,
    trainerId: payload.trainerId,
    imp: payload.imp,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const [user, setUser] = useState<AuthUser | null>(() => {
    const token = sessionStorage.getItem('accessToken')
    if (!token) return null
    try { return decodeUser(token) } catch { return null }
  })

  const [impersonatedTenantName, setImpersonatedTenantName] = useState<string | null>(
    () => sessionStorage.getItem('impersonatedTenantName')
  )

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await authApi.login(email, password)
    sessionStorage.setItem('accessToken', data.accessToken)
    setUser(decodeUser(data.accessToken))
  }, [])

  const loginWithToken = useCallback((accessToken: string) => {
    sessionStorage.setItem('accessToken', accessToken)
    setUser(decodeUser(accessToken))
  }, [])

  const logout = useCallback(async () => {
    try { await authApi.logout() } catch { /* ignore */ }
    sessionStorage.removeItem('accessToken')
    sessionStorage.removeItem('superadminToken')
    sessionStorage.removeItem('impersonatedTenantName')
    setUser(null)
    setImpersonatedTenantName(null)
  }, [])

  const impersonate = useCallback((token: string, tenantName: string) => {
    const current = sessionStorage.getItem('accessToken')
    if (current) sessionStorage.setItem('superadminToken', current)
    sessionStorage.setItem('accessToken', token)
    sessionStorage.setItem('impersonatedTenantName', tenantName)
    queryClient.clear()
    setUser(decodeUser(token))
    setImpersonatedTenantName(tenantName)
  }, [queryClient])

  const exitImpersonation = useCallback(() => {
    const superadminToken = sessionStorage.getItem('superadminToken')
    if (superadminToken) {
      sessionStorage.setItem('accessToken', superadminToken)
      sessionStorage.removeItem('superadminToken')
      setUser(decodeUser(superadminToken))
    }
    sessionStorage.removeItem('impersonatedTenantName')
    queryClient.clear()
    setImpersonatedTenantName(null)
  }, [queryClient])

  return (
    <AuthContext value={{ user, login, loginWithToken, logout, impersonate, exitImpersonation, impersonatedTenantName }}>
      {children}
    </AuthContext>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
